/* Oppdatert Assessments.jsx med støtte for den utvidede Nordic Model og 600-spørsmåls kapasitet */
import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
const { FiPlus, FiActivity, FiChevronRight, FiCheckCircle, FiInfo, FiLayers, FiList, FiSave } = FiIcons;
import { NORDIC_DIMENSIONS } from '../data/nordicModelData';
import QuestionSelector from '../components/assessments/QuestionSelector';

export const Assessments = () => {
  const location = useLocation();
const queryParams = new URLSearchParams(location.search);
const view = queryParams.get('view') || 'all';

const viewTitles = {
  all: 'Alle målinger',
  templates: 'Maler',
  invites: 'Invitasjoner',
  results: 'Resultater',
  departments: 'Avdelings-sammenligning'
};

const currentViewTitle = viewTitles[view] || 'Alle målinger';
  const [showCreator, setShowCreator] = useState(false);
  const [step, setStep] = useState(1);
  const [selectedDims, setSelectedDims] = useState(['psych_safety', 'workload']);
  const [selections, setSelections] = useState({});

  const handleUpdateQuestions = (dimId, questionIds) => {
    setSelections(prev => ({ ...prev, [dimId]: questionIds }));
  };

  const totalQuestions = Object.values(selections).reduce((acc, curr) => acc + curr.length, 0);

  return (
    <div className="max-w-7xl mx-auto space-y-8 pb-20">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <span className="bg-indigo-600 text-white text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-widest">
              Nordic Workplace Model v2.0
            </span>
          </div>
          <h1 className="text-4xl font-black text-slate-900 uppercase tracking-tighter">{currentViewTitle}</h1>
        </div>
        <button 
          onClick={() => setShowCreator(true)}
          className="flex items-center gap-2 bg-slate-900 text-white px-6 py-4 rounded-2xl font-black uppercase text-xs tracking-widest hover:bg-slate-800 transition-all shadow-xl shadow-slate-200"
        >
          <SafeIcon icon={FiPlus} /> Ny Kartlegging
        </button>
      </div>
{view === 'invites' && (
  <div className="bg-white border border-slate-200 rounded-3xl p-8">
    <h2 className="text-2xl font-black text-slate-900 mb-2">Invitasjoner</h2>
    <p className="text-slate-500 mb-6">
      Administrer utsendelser, påminnelser og svarstatus.
    </p>

    <div className="space-y-4">
      {[
        { name: 'Arbeidsmiljø Q2', sent: 124, answered: 98 },
        { name: 'Psykologisk trygghet', sent: 86, answered: 44 },
      ].map((invite, i) => (
        <div
          key={i}
          className="flex items-center justify-between p-4 rounded-2xl border border-slate-100 bg-slate-50"
        >
          <div>
            <p className="font-bold text-slate-900">{invite.name}</p>
            <p className="text-sm text-slate-500">
              Sendt: {invite.sent} • Besvart: {invite.answered}
            </p>
          </div>

          <button className="px-4 py-2 bg-indigo-600 text-white rounded-xl text-sm font-bold">
            Se detaljer
          </button>
        </div>
      ))}
    </div>
  </div>
)}
{view === 'templates' && (
  <div className="bg-white border border-slate-200 rounded-3xl p-8">
    <h2 className="text-2xl font-black text-slate-900 mb-2">Maler</h2>
    <p className="text-slate-500 mb-6">
      Velg ferdige kartleggingsmaler for ulike behov og risikoområder.
    </p>

    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {[
        'Full psykososial kartlegging',
        'Light / grovkartlegging',
        'Endrings-assessment',
        'Arbeidsmiljøsamtale',
        'Lederkartlegging',
        'Workshop / dialogspill'
      ].map((template, i) => (
        <div
          key={i}
          className="p-5 rounded-2xl border border-slate-100 bg-slate-50 hover:border-indigo-200 transition-all"
        >
          <p className="font-bold text-slate-900">{template}</p>
          <p className="text-xs text-slate-500 mt-2">
            Klar til bruk som utgangspunkt for ny måling.
          </p>
        </div>
      ))}
    </div>
  </div>
)}
      {/* Main Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-8 rounded-[2.5rem] border border-slate-100">
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Total Kapasitet</p>
          <p className="text-3xl font-black text-slate-900 tracking-tighter">600 <span className="text-sm text-slate-300">Spørsmål</span></p>
        </div>
        <div className="bg-white p-8 rounded-[2.5rem] border border-slate-100">
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Standard Skala</p>
          <p className="text-3xl font-black text-slate-900 tracking-tighter">1-5 <span className="text-sm text-slate-300">Likert</span></p>
        </div>
        <div className="bg-white p-8 rounded-[2.5rem] border border-slate-100">
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Dimensjoner</p>
          <p className="text-3xl font-black text-slate-900 tracking-tighter">12 <span className="text-sm text-slate-300">Kategorier</span></p>
        </div>
      </div>

      {/* Creator Overlay */}
      <AnimatePresence>
        {showCreator && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-slate-900/40 backdrop-blur-md z-[100] flex items-center justify-center p-4"
          >
            <motion.div 
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              className="bg-slate-50 w-full max-w-6xl max-h-[90vh] rounded-[3rem] shadow-2xl overflow-hidden flex flex-col"
            >
              {/* Creator Header */}
              <div className="bg-white p-8 border-b border-slate-100 flex justify-between items-center">
                <div className="flex items-center gap-6">
                  <div>
                    <h2 className="text-2xl font-black text-slate-900 uppercase tracking-tight">Konfigurer Modell</h2>
                    <div className="flex items-center gap-3 mt-1">
                      {[1, 2, 3].map(s => (
                        <div key={s} className="flex items-center gap-2">
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black ${
                            step === s ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-400'
                          }`}>{s}</div>
                          {s < 3 && <div className="w-4 h-0.5 bg-slate-100" />}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Totale spørsmål</p>
                    <p className="text-lg font-black text-indigo-600 tracking-tight">{totalQuestions} / 600</p>
                  </div>
                  <button onClick={() => setShowCreator(false)} className="w-12 h-12 rounded-full bg-slate-50 text-slate-400 hover:text-rose-600 transition-colors flex items-center justify-center">
                    <SafeIcon icon={FiPlus} className="rotate-45" size={24} />
                  </button>
                </div>
              </div>

              {/* Creator Content */}
              <div className="flex-1 overflow-y-auto p-10">
                {step === 1 && (
                  <div className="space-y-8">
                    <div className="max-w-2xl">
                      <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight mb-2">Velg Dimensjoner</h3>
                      <p className="text-slate-500 text-xs font-medium">Hvilke områder av det nordiske arbeidslivet skal denne kartleggingen dekke?</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {NORDIC_DIMENSIONS.map(dim => {
                        const isSelected = selectedDims.includes(dim.id);
                        return (
                          <button
                            key={dim.id}
                            onClick={() => setSelectedDims(prev => isSelected ? prev.filter(d => d !== dim.id) : [...prev, dim.id])}
                            className={`p-6 rounded-[2rem] border-2 text-left transition-all ${
                              isSelected ? 'bg-white border-indigo-600 shadow-xl shadow-indigo-100/50' : 'bg-white border-white hover:border-slate-200'
                            }`}
                          >
                            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-4 ${
                              isSelected ? 'bg-indigo-600 text-white' : 'bg-slate-50 text-slate-400'
                            }`}>
                              <SafeIcon icon={FiIcons[dim.icon]} size={24} />
                            </div>
                            <h4 className="text-xs font-black text-slate-900 uppercase tracking-widest">{dim.label}</h4>
                            <p className="text-[10px] text-slate-400 mt-2 font-medium leading-relaxed line-clamp-2">{dim.description}</p>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <div className="space-y-8">
                    <div className="max-w-2xl">
                      <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight mb-2">Velg Spørsmål</h3>
                      <p className="text-slate-500 text-xs font-medium">Tilpass kartleggingen ved å velge relevante spørsmål for hver dimensjon (maks 50 per felt).</p>
                    </div>
                    <div className="space-y-4">
                      {NORDIC_DIMENSIONS.filter(d => selectedDims.includes(d.id)).map(dim => (
                        <QuestionSelector 
                          key={dim.id} 
                          dimension={dim} 
                          onUpdate={handleUpdateQuestions}
                          initialSelected={selections[dim.id] || []}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Creator Footer */}
              <div className="p-8 bg-white border-t border-slate-100 flex justify-between items-center">
                <button 
                  disabled={step === 1}
                  onClick={() => setStep(s => s - 1)}
                  className={`px-8 py-4 rounded-2xl font-black uppercase text-[10px] tracking-widest transition-all ${
                    step === 1 ? 'opacity-0' : 'text-slate-400 hover:text-slate-900'
                  }`}
                >
                  Tilbake
                </button>
                <div className="flex gap-4">
                  <button className="flex items-center gap-2 px-8 py-4 border border-slate-200 rounded-2xl font-black uppercase text-[10px] tracking-widest text-slate-600 hover:bg-slate-50 transition-all">
                    <SafeIcon icon={FiSave} /> Lagre Mal
                  </button>
                  <button 
                    onClick={() => step < 3 ? setStep(s => s + 1) : setShowCreator(false)}
                    className="px-12 py-4 bg-indigo-600 text-white rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-indigo-700 shadow-xl shadow-indigo-100 transition-all"
                  >
                    {step === 2 ? 'Fullfør Oppsett' : 'Neste Steg'}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Assessments;